import TechnicianAssignmentField from '@/components/serviceTicket/assignment/TechnicianAssignmentField';
import { useGetRoutesQuery } from '@/store/api/services/operations/routes/routes';
import { useGetTaskTypesQuery } from '@/store/api/services/operations/taskTypes';
import { useGetServiceTicketEnumsQuery } from '@/store/api/services/resources/enums';
import type { Route } from '@/types/route';
import type { TaskType } from '@/types/serviceTicket';
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, useState } from 'react';
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
  useController,
} from 'react-hook-form';
import { Link } from 'react-router-dom';

import type { ServiceTicketCreateSchemaType } from '../schema';

interface TicketDetailsSectionProps {
  register: UseFormRegister<ServiceTicketCreateSchemaType>;
  control: Control<ServiceTicketCreateSchemaType>;
  errors: FieldErrors<ServiceTicketCreateSchemaType>;
  selectedTaskTypes: TaskType[];
  onSelectedTaskTypesChange: (taskTypes: TaskType[]) => void;
}

/**
 * Priority, description, tasks, technician, route, and schedule - the fields shared by every
 * service ticket, whether it's a single ticket or applied to a whole batch of bulk targets.
 */
export const TicketDetailsSection = ({
  register,
  control,
  errors,
  selectedTaskTypes,
  onSelectedTaskTypesChange,
}: TicketDetailsSectionProps) => {
  const [routeSearchQuery, setRouteSearchQuery] = useState('');
  const [openRoutesDropdown, setOpenRoutesDropdown] = useState(false);

  const { data: taskTypesResponse } = useGetTaskTypesQuery({
    pagination: {
      current_page: 1,
      items_per_page: 1000,
      sort_by: 'name',
      sort_ascending: true,
    },
  });
  const { data: routesData, isFetching: isLoadingRoutes } = useGetRoutesQuery(
    openRoutesDropdown
      ? {
          pagination: {
            current_page: 1,
            items_per_page: 25,
            sort_by: 'created_at',
            sort_ascending: false,
          },
          search_text: routeSearchQuery,
        }
      : skipToken,
  );
  const { data: serviceTicketEnumsData } = useGetServiceTicketEnumsQuery();

  const routes = useMemo(() => routesData?.data || [], [routesData]);
  const taskTypes = useMemo(() => taskTypesResponse?.data || [], [taskTypesResponse]);

  const { field: assignmentTypeField } = useController({
    name: 'assignment_type',
    control,
    defaultValue: 'internal',
  });
  const { field: assignedToField } = useController({ name: 'assigned_to', control });
  const { field: opsServiceIdField } = useController({ name: 'ops_service_id', control });

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Ticket Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" error={!!errors.priority}>
              <InputLabel>Priority</InputLabel>
              <Select
                {...register('priority')}
                displayEmpty
                defaultValue=""
                data-test="service-ticket-priority-select"
              >
                <MenuItem value="">Select Priority</MenuItem>
                {serviceTicketEnumsData?.data?.priorities?.map((priority) => (
                  <MenuItem key={priority} value={priority.toLowerCase()}>
                    {priority.charAt(0) + priority.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </Select>
              {errors.priority && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.priority.message}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Detailed Description
            </Typography>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextareaAutosize
                  {...field}
                  minRows={4}
                  maxRows={8}
                  data-test="service-ticket-description-textarea"
                  placeholder="Provide a detailed description of the issue."
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    padding: '12px 14px',
                    border: errors.description
                      ? '1px solid #d32f2f'
                      : '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    lineHeight: '1.5',
                    backgroundColor: 'transparent',
                    outline: 'none',
                    cursor: 'text',
                  }}
                />
              )}
            />
            {errors.description && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.description.message}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Autocomplete<TaskType, true>
              multiple
              disablePortal
              options={taskTypes}
              value={selectedTaskTypes}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => onSelectedTaskTypesChange(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tasks (optional)"
                  size="small"
                  placeholder="Add task types"
                  data-test="service-ticket-tasks-select"
                />
              )}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Add the expected tasks for this ticket. You can manage them later from the ticket.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Technician Assignment
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TechnicianAssignmentField
              assignmentType={assignmentTypeField.value ?? 'internal'}
              technicianId={assignedToField.value ?? ''}
              opsServiceId={opsServiceIdField.value ?? ''}
              onAssignmentTypeChange={(value) => {
                // Switching the type clears both selections so the submit carries only one.
                assignmentTypeField.onChange(value);
                assignedToField.onChange('');
                opsServiceIdField.onChange('');
              }}
              onTechnicianChange={assignedToField.onChange}
              onOpsServiceChange={opsServiceIdField.onChange}
              error={errors.assigned_to?.message || errors.ops_service_id?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="route"
              control={control}
              render={({ field }) => {
                const selectedRoute = routes.find((r) => r.id === field.value) || null;
                return (
                  <Autocomplete<Route>
                    disablePortal
                    open={openRoutesDropdown}
                    onOpen={() => setOpenRoutesDropdown(true)}
                    onClose={() => setOpenRoutesDropdown(false)}
                    options={routes}
                    loading={isLoadingRoutes}
                    value={selectedRoute}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      const routeLabel = option.name || `Route #${option.display_id}`;
                      return routeLabel;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === (value?.id ?? '')}
                    filterOptions={(options) => options}
                    onChange={(_, value) => {
                      field.onChange(value?.id || '');
                    }}
                    onInputChange={(_, value) => {
                      setRouteSearchQuery(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Route (optional)"
                        size="small"
                        fullWidth
                        placeholder="Search route by name or ID"
                        data-test="service-ticket-route-select"
                        InputProps={{
                          ...params.InputProps,
                        }}
                      />
                    )}
                  />
                );
              }}
            />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
              <Link to="/routes/create" style={{ color: 'inherit', textDecoration: 'underline' }}>
                Create a new route
              </Link>
            </Typography>
          </Grid>
          <Grid item xs={12} md={12}>
            <Typography variant="body2" color="text.secondary">
              Scheduled Date (optional)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              {...register('schedule_date')}
              InputLabelProps={{ shrink: true }}
              error={!!errors.schedule_date}
              helperText={errors.schedule_date?.message || 'When should the service ticket start?'}
              data-test="service-ticket-schedule-date-input"
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
